import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  LinearProgress,
  Chip,
  Button,
  alpha,
  Stack,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format, startOfMonth, endOfMonth, eachWeekOfInterval, addDays, isAfter } from 'date-fns';
import { ListFilter, PieChart as PieChartIcon, Download, BarChart2, Users } from 'lucide-react';
import { Programming } from '../types/ConcertTypes';

interface ProgramAnalysisProps {
  programs: Programming[];
}

interface FilterState {
  status: string[];
  genre: string[];
  ensembleMakeup: string[];
  expectedDraw: string[];
}

const STATUS_COLORS = {
  BOOKED: '#ef5350',
  PROVISIONAL: '#FFB300',
  CHECKING: '#42a5f5',
  AVAILABLE: '#66bb6a'
} as const;

const GENDER_COLORS = {
  male: '#2196f3',
  female: '#e91e63'
} as const;

const DRAW_COLORS = {
  LOW: '#ffb74d',
  MEDIUM: '#4fc3f7',
  HIGH: '#81c784',
  EXCEL: '#7e57c2'
} as const;

// Custom tooltip to ensure it stays within the chart container
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{ 
        bgcolor: 'background.paper', 
        p: 1.5, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 1
      }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          Count: {payload[0].value}
        </Typography>
      </Box>
    );
  }
  return null;
};

const ProgramAnalysis: React.FC<ProgramAnalysisProps> = ({ programs }) => {
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    genre: [],
    ensembleMakeup: [],
    expectedDraw: []
  });
  
  const [showDetailedGender, setShowDetailedGender] = useState(false);

  // Get all unique genres from programs
  const uniqueGenres = useMemo(() => 
    Array.from(new Set(programs.map(p => p.genre))).sort(),
    [programs]
  );

  const filteredPrograms = useMemo(() => {
    return programs.filter(program => {
      const matchesStatus = filters.status.length === 0 || filters.status.includes(program.status);
      const matchesGenre = filters.genre.length === 0 || filters.genre.includes(program.genre);
      const matchesMakeup = filters.ensembleMakeup.length === 0 || filters.ensembleMakeup.includes(program.ensembleMakeup);
      const matchesDraw = filters.expectedDraw.length === 0 || filters.expectedDraw.includes(program.expectedDraw);
      return matchesStatus && matchesGenre && matchesMakeup && matchesDraw;
    });
  }, [programs, filters]);

  const stats = useMemo(() => {
    // Status breakdown for pie chart
    const statusData = Object.keys(STATUS_COLORS).map(status => ({
      name: status,
      value: filteredPrograms.filter(p => p.status === status).length,
      color: STATUS_COLORS[status as keyof typeof STATUS_COLORS]
    })).filter(item => item.value > 0);

    // Simplified gender balance (Male vs Female-related)
    const simplifiedGenderData = [
      {
        name: "Male",
        value: filteredPrograms.filter(p => 
          p.ensembleMakeup === 'all-male' || p.ensembleMakeup === 'predominantly-male'
        ).length,
        color: GENDER_COLORS.male
      },
      {
        name: "Female",
        value: filteredPrograms.filter(p => 
          p.ensembleMakeup === 'all-female' || 
          p.ensembleMakeup === 'predominantly-female' || 
          p.ensembleMakeup === 'female-led' ||
          p.ensembleMakeup === 'mixed'
        ).length,
        color: GENDER_COLORS.female
      }
    ];

    // Detailed gender balance for pie chart
    const detailedGenderData = [
      {
        name: "All Male",
        value: filteredPrograms.filter(p => p.ensembleMakeup === 'all-male').length,
        color: '#2196f3'
      },
      {
        name: "All Female",
        value: filteredPrograms.filter(p => p.ensembleMakeup === 'all-female').length,
        color: '#e91e63'
      },
      {
        name: "Mixed",
        value: filteredPrograms.filter(p => p.ensembleMakeup === 'mixed').length,
        color: '#9c27b0'
      },
      {
        name: "Predominantly Male",
        value: filteredPrograms.filter(p => p.ensembleMakeup === 'predominantly-male').length,
        color: '#90caf9'
      },
      {
        name: "Female Led",
        value: filteredPrograms.filter(p => p.ensembleMakeup === 'female-led').length,
        color: '#f06292'
      },
      {
        name: "Predominantly Female",
        value: filteredPrograms.filter(p => p.ensembleMakeup === 'predominantly-female').length,
        color: '#f48fb1'
      }
    ].filter(item => item.value > 0);

    // Genre breakdown for bar chart
    const genreData = uniqueGenres
      .map(genre => ({
        name: genre,
        count: filteredPrograms.filter(p => p.genre === genre).length
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count);

    // Expected draw breakdown for bar chart
    const drawData = Object.keys(DRAW_COLORS)
      .map(draw => ({
        name: draw,
        value: filteredPrograms.filter(p => p.expectedDraw === draw).length,
        color: DRAW_COLORS[draw as keyof typeof DRAW_COLORS]
      }))
      .filter(item => item.value > 0);

    // Thursday calculation
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd });
    const thursdays = weeks.map(week => addDays(week, 4))
      .filter(date => isAfter(date, now));
    
    const availableThursdays = thursdays.length;
    const bookedThursdays = filteredPrograms.filter(p => p.status === 'BOOKED').length;
    const bookingProgress = availableThursdays > 0 
      ? (bookedThursdays / availableThursdays) * 100
      : 100;

    return {
      statusData,
      simplifiedGenderData,
      detailedGenderData,
      genreData,
      drawData,
      availableThursdays,
      bookedThursdays,
      bookingProgress
    };
  }, [filteredPrograms, uniqueGenres]);

  const handleFilterChange = (type: keyof FilterState) => (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFilters(prev => ({
      ...prev,
      [type]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: [],
      genre: [],
      ensembleMakeup: [],
      expectedDraw: []
    });
  };

  const exportData = () => {
    const csv = [
      ['Date', 'Artist', 'Genre', 'Status', 'Ensemble', 'Expected Draw', 'Price', 'Notes'].join(','),
      ...filteredPrograms.map(p => [
        format(new Date(p.date), 'yyyy-MM-dd'),
        `"${p.artistName}"`,
        `"${p.genre}"`,
        p.status,
        p.ensembleMakeup,
        p.expectedDraw,
        p.pricePoint,
        `"${p.notes || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `programming-analysis-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ListFilter size={24} />
              <Typography variant="h6">Filters</Typography>
            </Box>
          }
          action={
            <Button 
              variant="outlined" 
              size="small" 
              onClick={clearFilters}
              disabled={!Object.values(filters).some(f => f.length > 0)}
            >
              Clear Filters
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value} 
                          size="small"
                          sx={{ 
                            bgcolor: alpha(STATUS_COLORS[value as keyof typeof STATUS_COLORS], 0.1),
                            borderColor: STATUS_COLORS[value as keyof typeof STATUS_COLORS]
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {Object.keys(STATUS_COLORS).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Genre</InputLabel>
                <Select
                  multiple
                  value={filters.genre}
                  onChange={handleFilterChange('genre')}
                  label="Genre"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {uniqueGenres.map((genre) => (
                    <MenuItem key={genre} value={genre}>
                      {genre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ensemble Makeup</InputLabel>
                <Select
                  multiple
                  value={filters.ensembleMakeup}
                  onChange={handleFilterChange('ensembleMakeup')}
                  label="Ensemble Makeup"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value.replace(/-/g, ' ')} 
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {['all-male', 'all-female', 'mixed', 'predominantly-male', 'female-led', 'predominantly-female'].map((makeup) => (
                    <MenuItem key={makeup} value={makeup}>
                      {makeup.replace(/-/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Expected Draw</InputLabel>
                <Select
                  multiple
                  value={filters.expectedDraw}
                  onChange={handleFilterChange('expectedDraw')}
                  label="Expected Draw"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value} 
                          size="small"
                          sx={{ 
                            bgcolor: alpha(DRAW_COLORS[value as keyof typeof DRAW_COLORS], 0.1),
                            borderColor: DRAW_COLORS[value as keyof typeof DRAW_COLORS]
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {Object.keys(DRAW_COLORS).map((draw) => (
                    <MenuItem key={draw} value={draw}>
                      {draw}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <Grid container spacing={3}>
        {/* Booking Progress */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Programming Progress - {stats.availableThursdays} Thursday{stats.availableThursdays !== 1 ? 's' : ''} This Month
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download size={16} />}
                  onClick={exportData}
                >
                  Export Data
                </Button>
              </Stack>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.bookingProgress > 100 ? 100 : stats.bookingProgress} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: alpha(STATUS_COLORS.AVAILABLE, 0.2),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: STATUS_COLORS.BOOKED
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" mt={1}>
                {stats.bookedThursdays} of {stats.availableThursdays} Thursdays booked
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PieChartIcon size={24} />
                  <Typography variant="h6">Status Breakdown</Typography>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ height: 300, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {stats.statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      layout="horizontal" 
                      verticalAlign="bottom" 
                      align="center"
                      wrapperStyle={{ paddingTop: '20px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Gender Balance */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Users size={24} />
                  <Typography variant="h6">Gender Balance</Typography>
                </Box>
              }
              action={
                <FormControlLabel
                  control={
                    <Switch
                      checked={showDetailedGender}
                      onChange={(e) => setShowDetailedGender(e.target.checked)}
                      name="genderDetail"
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">Detailed</Typography>
                  }
                />
              }
            />
            <CardContent>
              <Box sx={{ height: 300, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={showDetailedGender ? stats.detailedGenderData : stats.simplifiedGenderData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {(showDetailedGender ? stats.detailedGenderData : stats.simplifiedGenderData).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      layout="horizontal" 
                      verticalAlign="bottom" 
                      align="center"
                      wrapperStyle={{ paddingTop: '20px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Genre Breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChart2 size={24} />
                  <Typography variant="h6">Genre Distribution</Typography>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={stats.genreData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Count" fill="#8884d8">
                      {stats.genreData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={`hsl(${(index * 360) / stats.genreData.length}, 70%, 50%)`} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Expected Draw */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChart2 size={24} />
                  <Typography variant="h6">Expected Draw Distribution</Typography>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={stats.drawData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Count">
                      {stats.drawData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProgramAnalysis;